import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { createPlanIdForTheDiv } from '../helpers';

interface Props {
  badgeText: string;
  planId: string;
}

// while was working on this component, the app has two experiences:
// 1. old experience - we use body as a container
// 2. rise experience (new one) - it uses a div with id `legacy-app-mount-container` as a container, all scrolling happens within rise container
const riseContainer = document.getElementById('legacy-app-mount-container');
const oldExperience = !riseContainer;

export const PlanBadgeText = ({ badgeText, planId }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const prevOverflowBody = useRef('');

  useLayoutEffect(() => {
    if (oldExperience) return () => '';

    // for rise experience -- set body overflow to hidden
    prevOverflowBody.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflowBody.current;
    };
  }, []);

  useEffect(() => {
    const scrollContainer = riseContainer ?? window;

    const updateBadgePosition = () => {
      const anchorFromDOM = document.getElementById(createPlanIdForTheDiv(planId));
      const badge = ref.current;

      if (!anchorFromDOM || !badge) {
        return;
      }

      const anchor = anchorFromDOM.getBoundingClientRect();

      if (oldExperience) {
        // we use body as a container
        badge.style.position = 'fixed';
      }

      badge.style.width = `${anchor.width}px`;

      const x = anchor.left;
      const y = anchor.top - badge.offsetHeight;

      badge.style.transform = `translate(${x}px, ${y}px)`;
      badge.style.opacity = '1';
    };

    let ticking = false;

    const handleEvent = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateBadgePosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    // The sequence of events is:
    // 1. The container is rendered with a padding-top of 0 (by default).
    // 2. The badges are rendered with their heights.
    // 3. The badge is positioned assuming that the container has `padding-top: 0`.
    // 4. The container is re-rendered with the correct padding-top.
    // 5. The badge is positioned incorrectly.
    // To prevent that, we set a timeout to ensure that the correct padding-top is applied on the container.
    // The padding-top value is the height of the heighest plan badge text div.
    // If we don't set a timeout, the badge will be positioned incorrectly.
    setTimeout(updateBadgePosition, 0);

    window.addEventListener('resize', handleEvent, { passive: true });
    scrollContainer.addEventListener('scroll', handleEvent, { passive: true });

    const mutationObserver = new MutationObserver(handleEvent);

    mutationObserver.observe(riseContainer ?? document.body, { childList: true, attributes: true, subtree: true });

    return () => {
      window.removeEventListener('resize', handleEvent);
      scrollContainer.removeEventListener('scroll', handleEvent);
      mutationObserver.disconnect();
    };
  }, [planId]);

  return createPortal(
    <div ref={ref} className="plan-item-container__badge-text">
      {badgeText}
    </div>,
    riseContainer ?? document.body
  );
};
